import React, { useCallback, useMemo, useState } from "react";
import { Button, List } from "@mui/material";
import { BaseWidgetProps, WidgetWrapper } from "shared-ui";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import useGetNewsArticles from "@features/news-center-feature/hooks/useGetNewsArticles";
import {
  ArticleDto,
  NewsCenterQuery,
  NewsCenterQuerySortOption,
} from "@lib/ShiOneClient";
import NewsItem from "./Components/NewsItem";
import ItemDetailDrawer from "@features/news-center-feature/components/ItemDetailDrawer";
import {
  IsNewsLink,
  NavigationDirection,
} from "@features/news-center-feature/utils/newsCenterUtils";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";

export default function NewsArticlesWidgetV2({
  pageLayoutWidget,
}: Readonly<BaseWidgetProps>) {
  const { hasPermission } = useClaims();
  const [selectedArticleId, setSelectedArticleId] = useState<number>(-1);

  const canReadNews = hasPermission(ShiOnePermission.newsCenterRead);

  const query = useMemo(
    () =>
      new NewsCenterQuery({
        page: 1,
        pageSize: 5,
        sortOption: new NewsCenterQuerySortOption({
          column: "modified",
          descending: true,
        }),
      }),
    [],
  );

  const { data, isLoading, isError } = useGetNewsArticles(query);

  const articles = data?.articles?.items || [];

  const handleArticleClick = useCallback(
    (article: ArticleDto) => {
      if (article.id && !IsNewsLink(article)) {
        setSelectedArticleId(article.id);
      }
    },
    [],
  );

  const navigateArticles = useCallback(
    (direction: NavigationDirection) => {
      const currentIndex = articles.findIndex(
        (a) => a.id === selectedArticleId,
      );
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (direction === "Up" && currentIndex > 0) {
        nextIndex = currentIndex - 1;
      } else if (direction === "Down" && currentIndex < articles.length - 1) {
        nextIndex = currentIndex + 1;
      }

      if (nextIndex !== currentIndex) {
        const nextArticle = articles[nextIndex];
        if (nextArticle.id) {
          setSelectedArticleId(nextArticle.id);
        }
      }
    },
    [articles, selectedArticleId],
  );

  if (!canReadNews) {
    return null;
  }

  return (
    <WidgetWrapper
      pageLayoutWidget={pageLayoutWidget}
      cardHeaderDivider
      noPadding
      error={isError}
      isEmpty={!isLoading && !articles.length}
      cardActions={
        <Button
          aria-label="View all news (opens in new tab)"
          href={AppendIfPrefixExists("/support-center/message-center/")}
        >
          VIEW ALL
        </Button>
      }
    >
      <List disablePadding aria-label="News and updates list">
        {articles.map((article) => (
          <NewsItem
            key={article.id}
            article={article}
            onClick={handleArticleClick}
          />
        ))}
      </List>
      {selectedArticleId !== -1 && (
        <ItemDetailDrawer
          articleId={selectedArticleId}
          setArticleId={setSelectedArticleId}
          drawerIsOpen={true}
          navigateArticles={navigateArticles}
        />
      )}
    </WidgetWrapper>
  );
}
